import type { ReactNode } from 'react';
import { X } from 'lucide-react';
import { Badge } from '@command-center/ui-system';
import { DATE_RANGES } from './log-filters';

export interface FilterField {
  key: string;
  label: string;
  /** Omit for a free-text input. */
  options?: Array<{ value: string; label: string }>;
  placeholder?: string;
}

/**
 * Filter toolbar for the log viewers.
 *
 * Values are owned by the caller and mirrored into the URL, so a filtered view
 * is a shareable link and survives a refresh. The bar renders only the fields
 * it is given — a portal with one organization is not shown an Organization
 * selector, because a control that cannot change the result is worse than no
 * control at all.
 */
export function LogFilterBar({
  fields,
  values,
  onChange,
  onClear,
  range,
  onRangeChange,
  searchPlaceholder = 'Search…',
  right,
}: {
  fields: FilterField[];
  values: Record<string, string | undefined>;
  onChange: (key: string, value: string | undefined) => void;
  onClear: () => void;
  range: string;
  onRangeChange: (range: string) => void;
  searchPlaceholder?: string;
  right?: ReactNode;
}) {
  const active = Object.entries(values).filter(([, v]) => v);

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        {/* Full width on its own row below `sm`. A min-width plus flex-1 on a
            wrapping row is what pushed the date selector off a 390px screen and
            made the whole page scroll sideways. */}
        <input
          type="search"
          value={values.search ?? ''}
          onChange={(e) => onChange('search', e.target.value || undefined)}
          placeholder={searchPlaceholder}
          aria-label={searchPlaceholder}
          className="h-9 w-full min-w-0 rounded-md border border-border bg-background px-2.5 text-[13px] outline-none focus:ring-2 focus:ring-ring sm:w-auto sm:min-w-[180px] sm:flex-1"
        />

        <select
          value={range}
          onChange={(e) => onRangeChange(e.target.value)}
          aria-label="Date range"
          className="h-9 min-w-0 rounded-md border border-border bg-background px-2 text-[13px] outline-none focus:ring-2 focus:ring-ring"
        >
          {DATE_RANGES.map((r) => (
            <option key={r.value} value={r.value}>
              {r.label}
            </option>
          ))}
        </select>

        {fields.map((field) =>
          field.options ? (
            <select
              key={field.key}
              value={values[field.key] ?? ''}
              onChange={(e) => onChange(field.key, e.target.value || undefined)}
              aria-label={field.label}
              className="h-9 min-w-0 max-w-[190px] rounded-md border border-border bg-background px-2 text-[13px] outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="">{field.label}: any</option>
              {field.options.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          ) : (
            <input
              key={field.key}
              value={values[field.key] ?? ''}
              onChange={(e) => onChange(field.key, e.target.value || undefined)}
              placeholder={field.placeholder ?? field.label}
              aria-label={field.label}
              className="h-9 w-[150px] min-w-0 max-w-full rounded-md border border-border bg-background px-2.5 text-[13px] outline-none focus:ring-2 focus:ring-ring"
            />
          ),
        )}

        {right}
      </div>

      {active.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          {active.map(([key, value]) => (
            <Badge key={key} variant="muted" className="gap-1 text-[11px]">
              {labelFor(fields, key)}: {value}
              <button
                type="button"
                onClick={() => onChange(key, undefined)}
                aria-label={`Clear ${labelFor(fields, key)} filter`}
                className="rounded-sm opacity-60 hover:opacity-100"
              >
                <X className="h-3 w-3" aria-hidden />
              </button>
            </Badge>
          ))}
          <button
            type="button"
            onClick={onClear}
            className="text-[11px] text-muted-foreground underline-offset-2 hover:underline"
          >
            Clear all
          </button>
        </div>
      )}
    </div>
  );
}

function labelFor(fields: FilterField[], key: string): string {
  if (key === 'search') return 'Search';
  return fields.find((f) => f.key === key)?.label ?? key;
}
